import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  InputLabel,
  FormControl,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import AddIcon from '@mui/icons-material/Add';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: '#1e293b',
  fontSize: '1.6rem',
  lineHeight: 1.3,
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: -16,
    top: 0,
    height: '100%',
    width: 4,
    background: 'linear-gradient(to bottom, #14b8a6, #0d9488)',
  },
  fontFamily: "'Inter', sans-serif",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(15, 23, 42, 0.1)',
    transition: 'all 0.3s ease',
    fontFamily: "'Inter', sans-serif",
    '&:hover': {
      borderColor: '#14b8a6',
      boxShadow: '0 4px 12px rgba(20, 184, 166, 0.2)',
    },
    '&.Mui-focused': {
      borderColor: '#14b8a6',
      boxShadow: '0 4px 16px rgba(20, 184, 166, 0.3)',
    },
  },
  '& .MuiInputBase-input': {
    padding: '14px 16px',
    fontSize: '0.95rem',
    color: '#1e293b',
    fontFamily: "'Inter', sans-serif",
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
    fontSize: '0.95rem',
    fontFamily: "'Inter', sans-serif",
    '&.Mui-focused': {
      color: '#14b8a6',
    },
  },
  '& .MuiFormHelperText-root': {
    color: '#f43f5e',
    fontSize: '0.85rem',
    fontFamily: "'Inter', sans-serif",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: 12,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(8px)',
  border: '1px solid rgba(15, 23, 42, 0.1)',
  transition: 'all 0.3s ease',
  fontFamily: "'Inter', sans-serif",
  '& .MuiSelect-select': {
    padding: '16px 20px',
    fontSize: '1rem',
    color: '#1e293b',
    fontFamily: "'Inter', sans-serif",
    display: 'flex',
    alignItems: 'center',
  },
  '&:hover': {
    borderColor: '#14b8a6',
    boxShadow: '0 4px 12px rgba(20, 184, 166, 0.2)',
  },
  '&.Mui-focused': {
    borderColor: '#14b8a6',
    boxShadow: '0 4px 16px rgba(20, 184, 166, 0.3)',
  },
  height: '56px',
  width: '200px',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  borderRadius: 12,
  background: 'linear-gradient(45deg, #14b8a6 30%, #0d9488 90%)',
  color: '#fff',
  textTransform: 'none',
  fontSize: '0.95rem',
  fontFamily: "'Inter', sans-serif",
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #0d9488 30%, #14b8a6 90%)',
    boxShadow: '0 8px 24px rgba(20, 184, 166, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
}));

const IconOnlyButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5),
  minWidth: '48px',
  borderRadius: 12,
  background: 'linear-gradient(45deg, #f43f5e 30%, #e11d48 90%)',
  color: '#fff',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #e11d48 30%, #f43f5e 90%)',
    boxShadow: '0 8px 24px rgba(244, 63, 94, 0.5)',
    transform: 'scale(1.05)',
  },
  '&:disabled': {
    background: '#e2e8f0',
    color: '#94a3b8',
    boxShadow: 'none',
  },
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const StyledList = styled(List)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(12px)',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(15, 23, 42, 0.1)',
  overflow: 'hidden',
  fontFamily: "'Inter', sans-serif",
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  transition: 'all 0.3s ease',
  borderLeft: '4px solid transparent',
  '&:hover': {
    background: 'rgba(20, 184, 166, 0.05)',
    borderLeftColor: '#14b8a6',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.1)',
  },
  fontFamily: "'Inter', sans-serif",
}));

const ListItemPrimaryText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: '#1e293b',
  fontSize: '1.1rem',
  fontFamily: "'Inter', sans-serif",
}));

const ListItemSecondaryText = styled(Typography)(({ theme }) => ({
  color: '#64748b',
  fontSize: '0.9rem',
  fontFamily: "'Inter', sans-serif",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: 8,
  textTransform: 'none',
  padding: '8px 16px',
  fontSize: '0.9rem',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
  fontFamily: "'Inter', sans-serif",
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 16px rgba(15, 23, 42, 0.2)',
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  background: 'rgba(15, 23, 42, 0.1)',
}));

const SearchInput = styled(Autocomplete)(({ theme }) => ({
  maxWidth: 400,
  marginBottom: theme.spacing(3),
  '& .MuiInputBase-root': {
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(15, 23, 42, 0.1)',
    transition: 'all 0.3s ease',
    fontFamily: "'Inter', sans-serif",
    '&:hover': {
      borderColor: '#14b8a6',
      boxShadow: '0 4px 12px rgba(20, 184, 166, 0.2)',
    },
    '&.Mui-focused': {
      borderColor: '#14b8a6',
      boxShadow: '0 4px 16px rgba(20, 184, 166, 0.3)',
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    fontSize: '0.95rem',
    color: '#1e293b',
    fontFamily: "'Inter', sans-serif",
  },
  '& .MuiInputLabel-root': {
    color: '#64748b',
    fontWeight: 500,
    fontSize: '0.95rem',
    fontFamily: "'Inter', sans-serif",
    '&.Mui-focused': {
      color: '#14b8a6',
    },
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.2)',
    maxWidth: 600,
    width: '100%',
    overflow: 'hidden',
    fontFamily: "'Inter', sans-serif",
    border: '1px solid rgba(20, 184, 166, 0.1)',
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #14b8a6 30%, #0d9488 90%)',
  padding: theme.spacing(2.5),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: '#fff',
  fontFamily: "'Inter', sans-serif",
  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)',
}));

const DialogTitleText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.4rem',
  fontFamily: "'Inter', sans-serif",
  letterSpacing: '0.5px',
}));

const DialogCloseButton = styled(IconButton)(({ theme }) => ({
  color: '#fff',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'rotate(90deg)',
  },
}));

const QuartiersSection = ({ restaurantId }) => {
  const [quartiers, setQuartiers] = useState([]);
  const [filteredQuartiers, setFilteredQuartiers] = useState([]);
  const [selectedQuartier, setSelectedQuartier] = useState(null);
  const [availableQuartiers, setAvailableQuartiers] = useState([]);
  const [quartierForm, setQuartierForm] = useState({
    id_quartier: '',
    frais_de_livraison: '',
    min_commande: '',
    delai_livraison: '',
  });
  const [quartierErrors, setQuartierErrors] = useState({});
  const [quartierEditId, setQuartierEditId] = useState(null);
  const [quartierLoading, setQuartierLoading] = useState(false);
  const [quartierError, setQuartierError] = useState('');
  const [quartierSuccess, setQuartierSuccess] = useState('');
  const [openQuartierDialog, setOpenQuartierDialog] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const fetchQuartiers = useCallback(async () => {
    setQuartierLoading(true);
    try {
      const [quartiersResponse, availableResponse] = await Promise.all([
        axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/quartiers`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
        axios.get(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/quartiers/available`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }),
      ]);
      setQuartiers(quartiersResponse.data);
      setFilteredQuartiers(quartiersResponse.data);
      setAvailableQuartiers(availableResponse.data);
    } catch (err) {
      setQuartierError(err.response?.data?.message || 'Erreur lors de la récupération des quartiers.');
    } finally {
      setQuartierLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    fetchQuartiers();
  }, [fetchQuartiers]);

  const handleSearchChange = useCallback((event, newValue) => {
    setSelectedQuartier(newValue);
    if (newValue) {
      setFilteredQuartiers([newValue]);
    } else {
      setFilteredQuartiers(quartiers);
    }
  }, [quartiers]);

  const handleQuartierChange = useCallback((e) => {
    const { name, value } = e.target;
    setQuartierForm((prev) => ({ ...prev, [name]: value }));
    setQuartierErrors((prev) => ({ ...prev, [name]: '' }));
  }, []);

  const validateQuartierForm = useCallback(() => {
    const errors = {};
    if (!quartierForm.id_quartier) errors.id_quartier = 'Le quartier est requis.';
    if (!quartierForm.frais_de_livraison) errors.frais_de_livraison = 'Les frais de livraison sont requis.';
    if (!quartierForm.min_commande) errors.min_commande = 'Le montant minimum de commande est requis.';
    if (!quartierForm.delai_livraison) errors.delai_livraison = 'Le délai de livraison est requis.';
    setQuartierErrors(errors);
    return Object.keys(errors).length === 0;
  }, [quartierForm]);

  const openQuartierForm = useCallback((quartier = null) => {
    if (quartier) {
      setQuartierForm({
        id_quartier: quartier.id,
        frais_de_livraison: quartier.frais_de_livraison || '',
        min_commande: quartier.min_commande || '',
        delai_livraison: quartier.delai_livraison || '',
      });
      setQuartierEditId(quartier.id);
    } else {
      setQuartierForm({ id_quartier: '', frais_de_livraison: '', min_commande: '', delai_livraison: '' });
      setQuartierEditId(null);
    }
    setQuartierErrors({});
    setOpenQuartierDialog(true);
  }, []);

  const handleDownloadExcel = useCallback(async () => {
    setActionLoading((prev) => ({ ...prev, downloadExcel: true }));
    try {
      const data = availableQuartiers.map((quartier) => ({
        id_quartier: quartier.id,
        nom_quartier: quartier.name,
        latitude: quartier.latitude || 0,
        longitude: quartier.longitude || 0,
        frais_de_livraison: '',
        min_commande: '',
        delai_livraison: '',
        etat: 1,
      }));

      const ws = XLSX.utils.json_to_sheet(data, {
        header: [
          'id_quartier',
          'nom_quartier',
          'latitude',
          'longitude',
          'frais_de_livraison',
          'min_commande',
          'delai_livraison',
          'etat',
        ],
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Quartiers');
      XLSX.writeFile(wb, `quartiers_ville_${restaurantId}.xlsx`);
      setQuartierSuccess('Modèle Excel téléchargé avec succès !');
    } catch (err) {
      setQuartierError('Erreur lors du téléchargement du fichier Excel.');
    } finally {
      setActionLoading((prev) => ({ ...prev, downloadExcel: false }));
    }
  }, [availableQuartiers, restaurantId]);

  const handleExcelUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExcelFile(file);
    setActionLoading((prev) => ({ ...prev, importExcel: true }));

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const quartiersToImport = [];
      for (const row of jsonData) {
        const {
          id_quartier,
          nom_quartier,
          latitude,
          longitude,
          frais_de_livraison,
          min_commande,
          delai_livraison,
          etat,
        } = row;

        if (!id_quartier || !nom_quartier) {
          setQuartierError('Chaque ligne doit contenir id_quartier et nom_quartier.');
          return;
        }

        if (frais_de_livraison == null || min_commande == null || delai_livraison == null) {
          setQuartierError('Frais de livraison, montant minimum et délai sont requis.');
          return;
        }

        if (etat !== 0 && etat !== 1) {
          setQuartierError("L'état doit être 0 (inactif) ou 1 (actif).");
          return;
        }

        const quartierExists = availableQuartiers.find((q) => q.id === id_quartier);
        if (!quartierExists) {
          setQuartierError(`Quartier avec id ${id_quartier} non trouvé dans la ville.`);
          return;
        }

        quartiersToImport.push({
          id_quartier,
          frais_de_livraison: parseFloat(frais_de_livraison) || 0,
          min_commande: parseInt(min_commande, 10) || 0,
          delai_livraison,
          etat,
        });
      }

      const response = await axios.post(
        `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/quartiers/bulk`,
        quartiersToImport,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );

      setQuartiers((prev) => [...prev, ...response.data.quartiers]);
      setFilteredQuartiers((prev) => [...prev, ...response.data.quartiers]);
      setQuartierSuccess('Quartiers importés avec succès !');
      setExcelFile(null);
    } catch (err) {
      setQuartierError(err.response?.data?.message || 'Erreur lors de l’importation du fichier Excel.');
    } finally {
      setActionLoading((prev) => ({ ...prev, importExcel: false }));
    }
  }, [availableQuartiers, restaurantId]);

  const handleQuartierSubmit = async (e) => {
    e.preventDefault();
    if (!validateQuartierForm()) return;

    setActionLoading((prev) => ({ ...prev, submitQuartier: true }));
    setQuartierError('');
    setQuartierSuccess('');

    try {
      const data = {
        id_quartier: parseInt(quartierForm.id_quartier, 10),
        frais_de_livraison: parseFloat(quartierForm.frais_de_livraison) || 0,
        min_commande: parseInt(quartierForm.min_commande, 10) || 0,
        delai_livraison: quartierForm.delai_livraison,
      };

      let response;
      if (quartierEditId) {
        response = await axios.put(
          `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/quartiers/${quartierEditId}`,
          data,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );
        setQuartiers((prev) =>
          prev.map((q) => (q.id === quartierEditId ? response.data.quartier : q))
        );
        setFilteredQuartiers((prev) =>
          prev.map((q) => (q.id === quartierEditId ? response.data.quartier : q))
        );
        setQuartierSuccess('Quartier mis à jour avec succès !');
      } else {
        response = await axios.post(
          `https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/quartiers`,
          data,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );
        setQuartiers((prev) => [...prev, response.data.quartier]);
        setFilteredQuartiers((prev) => [...prev, response.data.quartier]);
        setQuartierSuccess('Quartier ajouté avec succès !');
      }
      setOpenQuartierDialog(false);
    } catch (err) {
      setQuartierError(err.response?.data?.message || 'Erreur lors de la gestion du quartier.');
    } finally {
      setActionLoading((prev) => ({ ...prev, submitQuartier: false }));
    }
  };

  const handleQuartierDelete = async (quartierId) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce quartier ?')) return;
    setActionLoading((prev) => ({ ...prev, [`delete_${quartierId}`]: true }));
    setQuartierError('');
    setQuartierSuccess('');

    try {
      await axios.delete(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/quartiers/${quartierId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setQuartiers((prev) => prev.filter((q) => q.id !== quartierId));
      setFilteredQuartiers((prev) => prev.filter((q) => q.id !== quartierId));
      setQuartierSuccess('Quartier supprimé avec succès !');
    } catch (err) {
      setQuartierError(err.response?.data?.message || 'Erreur lors de la suppression du quartier.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [`delete_${quartierId}`]: false }));
    }
  };

  const handleDeleteAllQuartiers = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer tous les quartiers ? Cette action est irréversible.')) return;
    setActionLoading((prev) => ({ ...prev, deleteAll: true }));
    setQuartierError('');
    setQuartierSuccess('');

    try {
      await axios.delete(`https://apibackoffice.lalivraison.ma/api/restaurants/${restaurantId}/quartiers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setQuartiers([]);
      setFilteredQuartiers([]);
      setQuartierSuccess('Tous les quartiers ont été supprimés avec succès !');
    } catch (err) {
      setQuartierError(err.response?.data?.message || 'Erreur lors de la suppression de tous les quartiers.');
    } finally {
      setActionLoading((prev) => ({ ...prev, deleteAll: false }));
    }
  };

  const handleCloseSnackbar = () => {
    setQuartierError('');
    setQuartierSuccess('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <SectionTitle variant="h6">Quartiers</SectionTitle>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <StyledButton
            onClick={handleDownloadExcel}
            disabled={actionLoading.downloadExcel || quartierLoading}
            startIcon={actionLoading.downloadExcel ? null : <DownloadIcon />}
          >
            {actionLoading.downloadExcel ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : (
              'Télécharger Excel'
            )}
          </StyledButton>
          <StyledButton
            component="label"
            disabled={actionLoading.importExcel || quartierLoading}
            startIcon={actionLoading.importExcel ? null : <UploadIcon />}
          >
            {actionLoading.importExcel ? (
              <CircularProgress size={24} sx={{ color: '#fff' }} />
            ) : (
              'Importer Excel'
            )}
            <input
              type="file"
              accept=".xlsx, .xls"
              hidden
              onChange={handleExcelUpload}
            />
          </StyledButton>
          <StyledButton
            onClick={() => openQuartierForm()}
            disabled={quartierLoading}
            startIcon={<AddIcon />}
          >
            Ajouter un Quartier
          </StyledButton>
          <Tooltip title="Supprimer tous les quartiers">
            <IconOnlyButton
              onClick={handleDeleteAllQuartiers}
              disabled={actionLoading.deleteAll || quartierLoading || quartiers.length === 0}
              aria-label="Supprimer tous les quartiers"
            >
              {actionLoading.deleteAll ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                <DeleteIcon />
              )}
            </IconOnlyButton>
          </Tooltip>
        </Box>
      </Box>
      <SearchInput
        options={quartiers}
        getOptionLabel={(option) => option.name}
        value={selectedQuartier}
        onChange={handleSearchChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Rechercher un quartier"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#64748b' }} />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      {quartierLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress size={40} sx={{ color: '#14b8a6' }} />
        </Box>
      )}
      {quartierError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 12, fontFamily: "'Inter', sans-serif" }}>
          {quartierError}
        </Alert>
      )}
      {quartierSuccess && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 12, fontFamily: "'Inter', sans-serif" }}>
          {quartierSuccess}
        </Alert>
      )}
      {filteredQuartiers.length === 0 && !quartierLoading && (
        <Typography sx={{ textAlign: 'center', color: '#64748b', py: 4, fontSize: '1.1rem', fontFamily: "'Inter', sans-serif" }}>
          Aucun quartier trouvé. Ajoutez-en un ou modifiez votre recherche.
        </Typography>
      )}
      {filteredQuartiers.length > 0 && (
        <StyledList>
          <AnimatePresence>
            {filteredQuartiers.map((quartier, index) => (
              <motion.div
                key={quartier.id}
               

 initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <StyledListItem>
                  <ListItemText
                    primary={<ListItemPrimaryText>{quartier.name}</ListItemPrimaryText>}
                    secondary={
                      <>
                        <ListItemSecondaryText component="span">
                          Frais de livraison: {quartier.frais_de_livraison} MAD
                        </ListItemSecondaryText>
                        <br />
                        <ListItemSecondaryText component="span">
                          Min. commande: {quartier.min_commande} MAD
                        </ListItemSecondaryText>
                        <br />
                        <ListItemSecondaryText component="span">
                          Délai: {quartier.delai_livraison}
                        </ListItemSecondaryText>
                      </>
                    }
                  />
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <ActionButton
                      variant="contained"
                      sx={{
                        bgcolor: '#14b8a6',
                        color: '#fff',
                        '&:hover': { bgcolor: '#0d9488' },
                      }}
                      onClick={() => openQuartierForm(quartier)}
                      startIcon={<EditIcon />}
                      disabled={quartierLoading}
                    >
                      Modifier
                    </ActionButton>
                    <ActionButton
                      variant="contained"
                      sx={{
                        bgcolor: '#f43f5e',
                        color: '#fff',
                        '&:hover': { bgcolor: '#e11d48' },
                      }}
                      onClick={() => handleQuartierDelete(quartier.id)}
                      startIcon={actionLoading[`delete_${quartier.id}`] ? null : <DeleteIcon />}
                      disabled={actionLoading[`delete_${quartier.id}`] || quartierLoading}
                    >
                      {actionLoading[`delete_${quartier.id}`] ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                      ) : (
                        'Supprimer'
                      )}
                    </ActionButton>
                  </Box>
                </StyledListItem>
                {index < filteredQuartiers.length - 1 && <StyledDivider />}
              </motion.div>
            ))}
          </AnimatePresence>
        </StyledList>
      )}

      <StyledDialog
        open={openQuartierDialog}
        onClose={() => setOpenQuartierDialog(false)}
      >
        <DialogHeader>
          <DialogTitleText>
            {quartierEditId ? 'Modifier le Quartier' : 'Ajouter un Quartier'}
          </DialogTitleText>
          <DialogCloseButton onClick={() => setOpenQuartierDialog(false)}>
            <CloseIcon />
          </DialogCloseButton>
        </DialogHeader>
        <DialogContent sx={{ p: 4 }}>
          <Box component="form" onSubmit={handleQuartierSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!quartierErrors.id_quartier}>
                  <InputLabel id="quartier-label" sx={{ fontFamily: "'Inter', sans-serif", fontSize: '1rem' }}>
                    Quartier
                  </InputLabel>
                  <StyledSelect
                    labelId="quartier-label"
                    label="Quartier"
                    name="id_quartier"
                    value={quartierForm.id_quartier}
                    onChange={handleQuartierChange}
                    required
                  >
                    {availableQuartiers.map((quartier) => (
                      <MenuItem key={quartier.id} value={quartier.id} sx={{ fontSize: '0.95rem', py: 1.5 }}>
                        {quartier.name}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                  {quartierErrors.id_quartier && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, fontFamily: "'Inter', sans-serif" }}>
                      {quartierErrors.id_quartier}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  label="Frais de Livraison (MAD)"
                  name="frais_de_livraison"
                  value={quartierForm.frais_de_livraison}
                  onChange={handleQuartierChange}
                  fullWidth
                  type="number"
                  inputProps={{ step: '0.01' }}
                  required
                  error={!!quartierErrors.frais_de_livraison}
                  helperText={quartierErrors.frais_de_livraison}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  label="Montant Minimum de Commande (MAD)"
                  name="min_commande"
                  value={quartierForm.min_commande}
                  onChange={handleQuartierChange}
                  fullWidth
                  type="number"
                  required
                  error={!!quartierErrors.min_commande}
                  helperText={quartierErrors.min_commande}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  label="Délai de Livraison"
                  name="delai_livraison"
                  value={quartierForm.delai_livraison}
                  onChange={handleQuartierChange}
                  fullWidth
                  required
                  error={!!quartierErrors.delai_livraison}
                  helperText={quartierErrors.delai_livraison}
                />
              </Grid>
            </Grid>
            <DialogActions sx={{ mt: 4, justifyContent: 'space-between' }}>
              <Button
                onClick={() => setOpenQuartierDialog(false)}
                sx={{
                  color: '#64748b',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  fontFamily: "'Inter', sans-serif",
                  px: 3,
                  py: 1,
                  borderRadius: 8,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(15, 23, 42, 0.05)',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Annuler
              </Button>
              <StyledButton type="submit" disabled={actionLoading.submitQuartier || quartierLoading}>
                {actionLoading.submitQuartier ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : quartierEditId ? (
                  'Mettre à jour'
                ) : (
                  'Ajouter'
                )}
              </StyledButton>
            </DialogActions>
          </Box>
        </DialogContent>
      </StyledDialog>

      <Snackbar
        open={!!quartierError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "'Inter', sans-serif" }}
        >
          {quartierError}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!quartierSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)', fontFamily: "'Inter', sans-serif" }}
        >
          {quartierSuccess}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default memo(QuartiersSection);